/**
 * EL FORMULARIO DE INFORMACIÓN E INSCRIPCIÓN
 * =============================================================================
 * -----------------------------------------------------------------------------
 * LO MÁS IMPORTANTE DE ESTE FORMULARIO: NO MIENTE
 * -----------------------------------------------------------------------------
 * Todavía no hay un servicio configurado para recibir las solicitudes. Eso
 * significa que, si alguien lo llena, los datos NO van a ningún lado.
 *
 * Un formulario que dice «enviado con éxito» cuando no se envió nada es peor que
 * no tener formulario: la persona cree que ya te contactó, tú nunca te enteras, y
 * el jugador se queda sin respuesta. Nadie sabe qué pasó.
 *
 * Así que mientras falte el destino, el formulario:
 *
 *   · Valida igual, para que el error no aparezca después.
 *   · NO dice «enviado». Dice la verdad: que falta conectar el destino.
 *   · Ofrece el correo como alternativa que SÍ funciona.
 *
 * En cuanto se ponga la dirección en `src/config.ts`, empieza a enviar solo y el
 * mensaje cambia al de confirmación.
 *
 * -----------------------------------------------------------------------------
 * LO QUE SE MANDA
 * -----------------------------------------------------------------------------
 * Un POST con este cuerpo, que es lo que espera cualquier servicio de formularios:
 *
 *   { tutor, jugador, edad, telefono, correo, programa, mensaje, idioma }
 *
 * -----------------------------------------------------------------------------
 * SOBRE LOS DATOS PERSONALES
 * -----------------------------------------------------------------------------
 * Son datos de un menor de edad, así que se pide lo mínimo: quién es el tutor, el
 * nombre y la edad del jugador, y cómo contactarlos. Nada más. No se guarda nada
 * en el navegador ni se manda a ningún sitio que no sea el destino configurado.
 */

import { useId, useState, type FormEvent } from 'react';
import { CONFIG, estaPendiente } from '../config';
import { Icono } from './Iconos';
import { useIdioma } from '../i18n/useIdioma';
import type { Textos } from '../i18n/textos';

interface Campos {
  tutor: string;
  jugador: string;
  edad: string;
  telefono: string;
  correo: string;
  programa: string;
  mensaje: string;
}

const VACIO: Campos = {
  tutor: '',
  jugador: '',
  edad: '',
  telefono: '',
  correo: '',
  programa: '',
  mensaje: '',
};

type Errores = Partial<Record<keyof Campos, string>>;

/** Qué se está haciendo ahora mismo, para poder avisar en pantalla. */
type Estado = 'inicial' | 'enviando' | 'exito' | 'error' | 'sin-destino';

function Aviso({
  estado,
  t,
  correoListo,
  enlaceCorreo,
  intentado,
  errores,
  textos,
  alReintentar,
}: {
  estado: Estado;
  t: Textos['form'];
  correoListo: boolean;
  enlaceCorreo: string;
  intentado: boolean;
  errores: Errores;
  textos: Textos;
  /** Para volver a intentar el envío desde el aviso de error. */
  alReintentar: () => void;
}) {
  if (estado === 'exito') {
    return (
      <div className="aviso aviso--exito" role="status">
        <span className="aviso__icono">
          <Icono nombre="diamante" tam={20} />
        </span>
        <span>
          <strong className="aviso__titulo">{t.exitoTitulo}</strong>
          {t.exitoTexto}
        </span>
      </div>
    );
  }

  if (estado === 'error') {
    return (
      <div className="aviso aviso--error" role="alert">
        <span className="aviso__icono">
          <Icono nombre="cerrar" tam={20} />
        </span>
        <span>
          <strong className="aviso__titulo">{t.errorTitulo}</strong>
          {t.errorTexto}
          <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="boton boton--marco"
              style={{ minHeight: 42 }}
              onClick={alReintentar}
            >
              {t.reintentar}
            </button>
            {correoListo && (
              <a
                className="boton boton--marco"
                style={{ minHeight: 42 }}
                href={enlaceCorreo}
              >
                {t.escribirCorreo}
              </a>
            )}
          </div>
        </span>
      </div>
    );
  }

  if (estado === 'sin-destino') {
    return (
      <div className="aviso aviso--aviso" role="alert">
        <span className="aviso__icono">
          <Icono nombre="lista" tam={20} />
        </span>
        <span>
          <strong className="aviso__titulo">{t.sinDestinoTitulo}</strong>
          {t.sinDestinoTexto}
          {correoListo && (
            <div style={{ marginTop: 12 }}>
              <a className="boton boton--oro" style={{ minHeight: 42 }} href={enlaceCorreo}>
                {t.escribirCorreo}
                <Icono nombre="flecha" tam={16} />
              </a>
            </div>
          )}
        </span>
      </div>
    );
  }

  // Si hay campos con error, se listan arriba.
  const cuantos = Object.keys(errores).length;
  if (intentado && cuantos > 0) {
    return (
      <div className="aviso aviso--error" role="alert">
        <span className="aviso__icono">
          <Icono nombre="cerrar" tam={20} />
        </span>
        <span>
          {cuantos === 1
            ? textos.form.hayUnCampo
            : `Hay ${cuantos} campos por corregir. Están marcados abajo.`}
        </span>
      </div>
    );
  }

  return null;
};

// ===========================================================================
// Un campo, para no repetir el mismo bloque siete veces
// ===========================================================================

function Campo({
  idBase,
  valor,
  error,
  onCambiar,
  nombre,
  etiqueta,
  tipo = 'text',
  obligatorio = false,
  area = false,
  placeholder,
  ancho = false,
  listas,
}: {
  nombre: keyof Campos;
  etiqueta: string;
  idBase: string;
  valor: string;
  error?: string;
  onCambiar: (nombre: keyof Campos, valor: string) => void;
  tipo?: string;
  obligatorio?: boolean;
  area?: boolean;
  placeholder?: string;
  ancho?: boolean;
  listas?: string[];
}) {
  const id = `${idBase}-${nombre}`;
  const idError = `${id}-error`;

  return (
    <div className={`campo ${ancho ? 'campo--ancho' : ''}`}>
      <label className="campo__etiqueta" htmlFor={id}>
        {etiqueta}
        {obligatorio && (
          <span className="campo__obligatorio" aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </label>

      {area ? (
        <textarea
          id={id}
          className={`campo__control campo__control--area ${
            error ? 'campo__control--error' : ''
          }`}
          value={valor}
          onChange={(e) => onCambiar(nombre, e.target.value)}
          placeholder={placeholder}
          aria-required={obligatorio}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? idError : undefined}
        />
      ) : listas ? (
        <select
          id={id}
          className={`campo__control ${error ? 'campo__control--error' : ''}`}
          value={valor}
          onChange={(e) => onCambiar(nombre, e.target.value)}
          aria-required={obligatorio}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? idError : undefined}
        >
          <option value="">{placeholder ?? '—'}</option>
          {listas.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          className={`campo__control ${error ? 'campo__control--error' : ''}`}
          type={tipo}
          inputMode={
            tipo === 'tel' ? 'tel' : tipo === 'email' ? 'email' : tipo === 'number' ? 'numeric' : undefined
          }
          value={valor}
          onChange={(e) => onCambiar(nombre, e.target.value)}
          placeholder={placeholder}
          aria-required={obligatorio}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? idError : undefined}
        />
      )}

      {error && (
        <span className="campo__error" id={idError} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export function Formulario() {
  const { textos, idioma } = useIdioma();
  const t = textos.form;
  const idBase = useId();

  const [campos, setCampos] = useState<Campos>(VACIO);
  const [errores, setErrores] = useState<Errores>({});
  const [estado, setEstado] = useState<Estado>('inicial');
  /** Se enseña el resumen de errores solo después del primer intento. */
  const [intentado, setIntentado] = useState(false);

  const hayEndpoint = Boolean(CONFIG.formulario.endpoint);
  const correoListo = !estaPendiente(CONFIG.formulario.correoDestino);

  /** Los programas que se pueden elegir, sacados de la propia lista de programas. */
  const programas = textos.programas.lista.map((p) => p.titulo);

  // ===========================================================================
  // Validación
  // ===========================================================================
  const validar = (c: Campos): Errores => {
    const e: Errores = {};

    if (!c.tutor.trim()) e.tutor = t.obligatorio;
    if (!c.jugador.trim()) e.jugador = t.obligatorio;

    const edad = Number(c.edad);
    if (!c.edad.trim()) {
      e.edad = t.obligatorio;
    } else if (Number.isNaN(edad) || edad < 4 || edad > 60) {
      e.edad = t.edadInvalida;
    }

    // El teléfono: se cuentan solo los dígitos, porque la gente escribe guiones,
    // espacios y paréntesis. Pedir un formato exacto sería estorbar.
    const digitos = c.telefono.replace(/\D/g, '');
    if (!c.telefono.trim()) {
      e.telefono = t.obligatorio;
    } else if (digitos.length < 10) {
      e.telefono = t.telefonoInvalido;
    }

    // El correo: una comprobación sencilla pero suficiente. No se intenta validar
    // un correo con una expresión regular perfecta, porque no existe.
    if (!c.correo.trim()) {
      e.correo = t.obligatorio;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(c.correo.trim())) {
      e.correo = t.correoInvalido;
    }

    return e;
  };

  const cambiar = (campo: keyof Campos, valor: string) => {
    setCampos((previo) => ({ ...previo, [campo]: valor }));

    // Si el campo ya tenía error, se vuelve a comprobar al escribir: así el aviso
    // desaparece en cuanto se corrige, sin esperar a otro intento.
    if (intentado) {
      const nuevos = validar({ ...campos, [campo]: valor });
      setErrores((previo) => ({ ...previo, [campo]: nuevos[campo] }));
    }
  };

  // ===========================================================================
  // Enviar
  // ===========================================================================
  const enviar = async (ev: FormEvent) => {
    ev.preventDefault();
    setIntentado(true);

    const e = validar(campos);
    setErrores(e);

    if (Object.keys(e).length > 0) {
      // Se lleva el foco al primer campo con error: si no, en un formulario largo
      // la persona no sabe qué le falta.
      const primero = Object.keys(e)[0];
      document.getElementById(`${idBase}-${primero}`)?.focus();
      return;
    }

    // Sin destino configurado no se intenta enviar: se dice la verdad.
    if (!hayEndpoint) {
      setEstado('sin-destino');
      return;
    }

    setEstado('enviando');

    try {
      const respuesta = await fetch(CONFIG.formulario.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          tutor: campos.tutor.trim(),
          jugador: campos.jugador.trim(),
          edad: Number(campos.edad),
          telefono: campos.telefono.trim(),
          correo: campos.correo.trim(),
          programa: campos.programa.trim() || null,
          mensaje: campos.mensaje.trim() || null,
          idioma,
        }),
      });

      if (!respuesta.ok) {
        throw new Error(`El servidor respondió ${respuesta.status}`);
      }

      setEstado('exito');
      setCampos(VACIO);
      setIntentado(false);
    } catch (error) {
      // Se deja en la consola el detalle técnico, para poder diagnosticarlo.
      console.error('[formulario] No se pudo enviar:', error);
      setEstado('error');
    }
  };

  // ===========================================================================
  // El enlace de correo, que es la alternativa real
  // ===========================================================================
  const enlaceCorreo = `mailto:${CONFIG.formulario.correoDestino}?subject=${encodeURIComponent(
    textos.form.correoAsunto
  )}&body=${encodeURIComponent(
    [
      `${t.tutor}: ${campos.tutor}`,
      `${t.jugador}: ${campos.jugador}`,
      `${t.edad}: ${campos.edad}`,
      `${t.telefono}: ${campos.telefono}`,
      `${t.correo}: ${campos.correo}`,
      campos.programa ? `${t.programa}: ${campos.programa}` : '',
      '',
      campos.mensaje,
    ]
      .filter(Boolean)
      .join('\n')
  )}`;

  // ===========================================================================
  // El aviso de arriba
  // ===========================================================================

  return (
    <form className="formulario" id="formulario" onSubmit={enviar} noValidate>
      <h3 className="formulario__titulo">{t.titulo}</h3>
      <p className="formulario__bajada">{t.bajada}</p>

      <div style={{ marginTop: 24 }}>
        <Aviso
          estado={estado}
          t={t}
          correoListo={correoListo}
          enlaceCorreo={enlaceCorreo}
          intentado={intentado}
          errores={errores}
          textos={textos}
          alReintentar={() => setEstado('inicial')}
        />
      </div>

      <div className="formulario__reja">
        <Campo idBase={idBase} valor={campos.tutor} error={errores.tutor} onCambiar={cambiar} nombre="tutor" etiqueta={t.tutor} obligatorio />
        <Campo idBase={idBase} valor={campos.jugador} error={errores.jugador} onCambiar={cambiar} nombre="jugador" etiqueta={t.jugador} obligatorio />
        <Campo idBase={idBase} valor={campos.edad} error={errores.edad} onCambiar={cambiar} nombre="edad" etiqueta={t.edad} tipo="number" obligatorio />
        <Campo idBase={idBase} valor={campos.telefono} error={errores.telefono} onCambiar={cambiar} nombre="telefono" etiqueta={t.telefono} tipo="tel" obligatorio />
        <Campo idBase={idBase} valor={campos.correo} error={errores.correo} onCambiar={cambiar} nombre="correo" etiqueta={t.correo} tipo="email" obligatorio />
        <Campo
          idBase={idBase}
          valor={campos.programa}
          error={errores.programa}
          onCambiar={cambiar}
          nombre="programa"
          etiqueta={t.programa}
          listas={programas}
          placeholder={t.programaPlaceholder}
        />
        <Campo
          idBase={idBase}
          valor={campos.mensaje}
          error={errores.mensaje}
          onCambiar={cambiar}
          nombre="mensaje"
          etiqueta={t.mensaje}
          area
          ancho
          placeholder={t.mensajePlaceholder}
        />
      </div>

      <div className="formulario__acciones">
        <button
          type="submit"
          className="boton boton--oro"
          disabled={estado === 'enviando'}
          aria-busy={estado === 'enviando'}
        >
          {estado === 'enviando' ? t.enviando : t.enviar}
          {estado !== 'enviando' && <Icono nombre="flecha" tam={16} />}
        </button>
      </div>
    </form>
  );
}
